import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import {
  Box,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  styled,
} from '@mui/material';
import { stepConnectorClasses } from '@mui/material/StepConnector';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';

import AddDeviceToUserAccount from './AddDeviceToUserAccount';
import AddNewUser from './AddNewUser';
import AddUserSuccess from './AddUserSuccess';
import AssignRoleToUser from './AssignRoleToUser';

type AddUserToOrganizationStage = 'email' | 'role' | 'devices' | 'success';

const LineConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.vars.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.vars.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.vars.palette.grey[400],
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

interface AddMemberToOrganizationModalState {
  stage: AddUserToOrganizationStage;
  email: string;
  role: string;
  devices: number[];
}

export interface AddMemberToOrganizationModalStageProps {
  open: boolean;
  state: AddMemberToOrganizationModalState;
  setState: Dispatch<SetStateAction<AddMemberToOrganizationModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
  organizationId: number | null;
  selectedDevices: number[];
}

const StageComponents: Record<
  AddUserToOrganizationStage,
  FC<AddMemberToOrganizationModalStageProps>
> = {
  email: AddNewUser,
  role: AssignRoleToUser,
  devices: AddDeviceToUserAccount,
  success: AddUserSuccess,
};

interface Props extends DefaultPopupProps {
  organizationId: number | null;
  selectedDevices: number[];
}

const AddMemberToOrganizationModal: FC<Props> = ({
  onClose,
  open,
  organizationId,
  selectedDevices,
}) => {
  const { t } = useTranslation();

  const steps = [
    t('common:pages.organization_overview.add_user.information'),
    t('common:pages.organization_overview.add_user.role'),
    t('common:pages.organization_overview.add_user.devices'),
  ];

  const [state, setState] = useState<AddMemberToOrganizationModalState>({
    stage: 'email',
    email: '',
    role: '',
    devices: [],
  });

  const stepMap: Record<string, number> = {
    email: 0,
    role: 1,
    devices: 2,
  };

  const activeStep = (step: string): number => {
    return stepMap[step] ?? 2;
  };

  useEffect(() => {
    if (!open) {
      setState(prev => ({ ...prev, email: '', role: '', devices: [] }));
    }
  }, [open]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    if (state.stage === 'email') {
      setState(prev => ({
        ...prev,
        email: '',
        role: '',
        devices: [],
        addDevice: '',
      }));

      onClose();
    } else if (state.stage === 'role') {
      setState(prev => ({
        ...prev,
        stage: 'email',
      }));
    } else if (state.stage === 'devices') {
      setState(prev => ({
        ...prev,
        stage: 'role',
      }));
    }
  }, [onClose, state]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={
        state.stage !== 'email' ? <ArrowBackRoundedIcon /> : undefined
      }
      hideCloseButton={state.stage === 'success'}
    >
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        width="100%"
        data-testid="add-member-to-organization-modal"
      >
        {state.stage !== 'success' ? (
          <Box pb={4} width="100%" data-testid="stepper-block">
            <Stepper
              alternativeLabel
              activeStep={activeStep(state.stage)}
              sx={{ marginX: -1 }}
              connector={<LineConnector />}
            >
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        ) : null}
        <StageComponent
          open={open}
          setState={setState}
          state={state}
          onClose={onClose}
          onCloseProxy={onCloseProxy}
          organizationId={organizationId}
          selectedDevices={selectedDevices}
        />
      </Box>
    </Modal>
  );
};

export default AddMemberToOrganizationModal;
